import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Sortable from 'sortablejs';
import axios from 'axios';
import * as Icons from 'react-feather';
import DataTable from 'react-data-table-component';
import { Link } from 'react-router-dom';
import { appendApplicationId, getApiBaseUrl } from '../../../utils/apiConfig';
import { getStorageValues } from '../../../shared/CommonFunctions';
import FileUploadModal from '../../../shared/file-upload/FileUploadModal';
import SKAlerts, { CreateAlert } from '../../../shared/common-alerts/SKAlerts';
import { ReactComponent as SortArrow } from '../../../shared/img/chevron-up-down.svg';
import { buildImageUrlFromUpload } from '../product/utils/productHelpers';
import {
  getNextDisplayOrder,
  isBannerActive,
  resolveBannerImageUrl,
  sortBannerRows,
} from './utils/bannerHelpers';

const emptyDraft = () => ({
  BannerId: null,
  BannerType: 'hero',
  ImageUrl: '',
  Title: '',
  ProductId: null,
  IsActive: true,
});

function HomepageBannerTab({ bannerType = 'hero' }) {
  const [alert, setAlert] = useState(null);
  const [bannerRows, setBannerRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [orderSaving, setOrderSaving] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [draft, setDraft] = useState(() => ({ ...emptyDraft(), BannerType: bannerType }));
  const tableWrapperRef = useRef(null);
  const sortableRef = useRef(null);
  const bannerRowsRef = useRef(bannerRows);

  useEffect(() => {
    bannerRowsRef.current = bannerRows;
  }, [bannerRows]);

  const fetchBanners = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${getApiBaseUrl()}/ec-homepage-banner/getAll`, {
        params: { BannerType: bannerType },
      });
      setBannerRows(sortBannerRows(response.data?.data || []));
    } catch (err) {
      console.error(err);
      CreateAlert('Failed to load homepage banners', 'Error', setAlert);
    } finally {
      setLoading(false);
    }
  }, [bannerType]);

  useEffect(() => {
    fetchBanners();
  }, [fetchBanners]);

  const resetDraft = () => {
    setDraft({ ...emptyDraft(), BannerType: bannerType });
  };

  const rowOrderKey = useMemo(
    () => bannerRows.map((row) => row.BannerId).join(','),
    [bannerRows],
  );

  const persistDisplayOrder = useCallback(async (orderedRows, previousRows) => {
    setOrderSaving(true);
    try {
      await axios.post(`${getApiBaseUrl()}/ec-homepage-banner/update-order`, {
        OrderList: orderedRows.map((row, index) => ({
          BannerId: row.BannerId,
          DisplayOrder: index + 1,
        })),
        UserId: getStorageValues('userId'),
      });
      CreateAlert('Display order updated', 'Success', setAlert);
      await fetchBanners();
    } catch (err) {
      console.error(err);
      setBannerRows(previousRows);
      CreateAlert('Unable to update display order', 'Error', setAlert);
    } finally {
      setOrderSaving(false);
    }
  }, [fetchBanners]);

  useEffect(() => {
    const wrapper = tableWrapperRef.current;
    const container = wrapper?.querySelector('.rdt_TableBody');
    if (!container) return;

    if (sortableRef.current) {
      sortableRef.current.destroy();
      sortableRef.current = null;
    }

    sortableRef.current = Sortable.create(container, {
      handle: '.drag-handle',
      draggable: '.rdt_TableRow',
      animation: 150,
      scroll: true,
      disabled: orderSaving || loading,
      onEnd: (evt) => {
        if (orderSaving || evt.oldIndex === evt.newIndex) return;

        const previousRows = bannerRowsRef.current;
        const updated = [...previousRows];
        const [moved] = updated.splice(evt.oldIndex, 1);
        updated.splice(evt.newIndex, 0, moved);
        const withOrder = updated.map((row, index) => ({
          ...row,
          DisplayOrder: index + 1,
        }));

        setBannerRows(withOrder);
        persistDisplayOrder(withOrder, previousRows);
      },
    });

    return () => {
      if (sortableRef.current) {
        sortableRef.current.destroy();
        sortableRef.current = null;
      }
    };
  }, [loading, orderSaving, rowOrderKey, bannerRows.length, persistDisplayOrder]);

  const saveBanner = async () => {
    if (!draft.ImageUrl?.trim()) {
      CreateAlert('Image URL is required', 'Warning', setAlert);
      return;
    }

    let displayOrder = getNextDisplayOrder(bannerRows);
    if (draft.BannerId) {
      const existing = bannerRows.find((row) => row.BannerId === draft.BannerId);
      displayOrder = existing?.DisplayOrder ?? displayOrder;
    }

    try {
      const response = await axios.post(`${getApiBaseUrl()}/ec-homepage-banner/save`, {
        BannerId: draft.BannerId,
        BannerType: bannerType,
        ImageUrl: draft.ImageUrl.trim(),
        Title: null,
        ProductId: null,
        DisplayOrder: displayOrder,
        IsActive: draft.IsActive ? 1 : 0,
        UserId: getStorageValues('userId'),
      });
      const result = response.data?.data;
      if (result?.success === 1) {
        CreateAlert(result.message || 'Hero slide saved', 'Success', setAlert);
        resetDraft();
        await fetchBanners();
      } else {
        CreateAlert(result?.message || 'Failed to save hero slide', 'Error', setAlert);
      }
    } catch (err) {
      console.error(err);
      CreateAlert('Failed to save hero slide', 'Error', setAlert);
    }
  };

  const deleteBanner = useCallback(async (bannerId) => {
    try {
      const response = await axios.post(`${getApiBaseUrl()}/ec-homepage-banner/delete`, {
        BannerId: bannerId,
        UserId: getStorageValues('userId'),
      });
      if (response.data?.data?.success === 1) {
        if (draft.BannerId === bannerId) {
          resetDraft();
        }
        CreateAlert('Hero slide deleted', 'Success', setAlert);
        await fetchBanners();
      }
    } catch (err) {
      console.error(err);
      CreateAlert('Failed to delete hero slide', 'Error', setAlert);
    }
  }, [draft.BannerId, fetchBanners]);

  const editRow = useCallback((row) => {
    setDraft({
      BannerId: row.BannerId,
      BannerType: bannerType,
      ImageUrl: row.ImageUrl || '',
      Title: row.Title || '',
      ProductId: row.ProductId || null,
      IsActive: isBannerActive(row),
    });
  }, [bannerType]);

  const columns = useMemo(() => [
    {
      name: <div className="titlename" />,
      cell: () => (
        <span className="drag-handle" style={{ cursor: orderSaving ? 'not-allowed' : 'grab' }}>
          <Icons.Menu size={16} />
        </span>
      ),
      width: '30px',
      sortable: false,
    },
    {
      name: (
        <div className="titlename">
          <span>Preview</span>
        </div>
      ),
      cell: (row) =>
        row.ImageUrl ? (
          <img
            src={resolveBannerImageUrl(row.ImageUrl)}
            alt=""
            style={{ maxHeight: 48, maxWidth: 80 }}
          />
        ) : (
          '-'
        ),
      width: '100px',
      sortable: false,
    },
    {
      name: (
        <div className="titlename">
          <span>Active</span>
        </div>
      ),
      cell: (row) => (isBannerActive(row) ? 'Yes' : 'No'),
      sortable: false,
      sortIcon: <SortArrow />,
    },
    {
      name: <div className="titlename">Actions</div>,
      cell: (row) => (
        <div className="edit-delete-action">
          <Link
            className="me-2 p-2"
            to=""
            onClick={(e) => {
              e.preventDefault();
              editRow(row);
            }}
          >
            <Icons.Edit className="feather-edit" />
          </Link>
          <Link
            className="confirm-text p-2"
            to=""
            onClick={(e) => {
              e.preventDefault();
              deleteBanner(row.BannerId);
            }}
          >
            <Icons.Trash2 className="feather-trash-2" />
          </Link>
        </div>
      ),
      center: true,
      width: '120px',
      sortable: false,
    },
  ], [deleteBanner, editRow, orderSaving]);

  return (
    <>
      <SKAlerts alert={alert} />
      <div className="card mb-3 sk-homepage-banner-form">
        <div className="card-body">
          <h6 className="mb-2">{draft.BannerId ? 'Edit Hero Slide' : 'Add Hero Slide'}</h6>
          <div className="row">
            <div className="col-lg-5">
              <label className="form-label">Image URL</label>
              <div className="input-group">
                <input
                  type="text"
                  className="form-control"
                  value={draft.ImageUrl}
                  onChange={(e) => setDraft((p) => ({ ...p, ImageUrl: e.target.value }))}
                  placeholder="Select single or multiple images"
                />
                <button
                  type="button"
                  className="btn btn-outline-primary d-flex align-items-center justify-content-center"
                  onClick={() => setShowUpload(true)}
                  title="Upload images"
                  aria-label="Upload images"
                >
                  <Icons.Upload size={18} />
                </button>
              </div>
              {draft.ImageUrl ? (
                <img
                  src={resolveBannerImageUrl(draft.ImageUrl)}
                  alt=""
                  className="mt-2 border rounded"
                  style={{ maxHeight: 80, maxWidth: 120 }}
                />
              ) : null}
            </div>
            <div className="col-lg-3 d-flex align-items-end">
              <div className="form-check mb-2">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="banner-active-hero"
                  checked={draft.IsActive}
                  onChange={(e) => setDraft((p) => ({ ...p, IsActive: e.target.checked }))}
                />
                <label className="form-check-label" htmlFor="banner-active-hero">Active</label>
              </div>
            </div>
            <div className="col-lg-4 d-flex align-items-end gap-2">
              <button type="button" className="btn btn-submit" onClick={saveBanner}>
                {draft.BannerId ? 'Save' : 'Add'}
              </button>
              {draft.BannerId ? (
                <button type="button" className="btn btn-cancel" onClick={resetDraft}>Cancel</button>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      <div className="card table-list-card sk-homepage-banner-table" ref={tableWrapperRef}>
        <div className="card-body">
          <DataTable
            className="table-responsive product-list"
            columns={columns}
            data={bannerRows}
            keyField="BannerId"
            pagination={false}
            fixedHeader
            fixedHeaderScrollHeight="320px"
            sortIcon={<SortArrow />}
            persistTableHead
            progressPending={loading}
            noDataComponent={<span className="text-muted">No hero slides added.</span>}
          />
        </div>
      </div>

      <FileUploadModal
        show={showUpload}
        onClose={() => setShowUpload(false)}
        categories={['homepage-banners']}
        onConfirm={async ({ files }) => {
          if (!files?.length) return;

          try {
            const formDataToSend = new FormData();
            formDataToSend.append('entityId', draft.BannerId || 0);
            formDataToSend.append('entityType', 'ec_homepagebanner');
            formDataToSend.append('fileCategory', 'homepage-banners');
            formDataToSend.append('UserId', getStorageValues('userId'));
            files.forEach((file) => formDataToSend.append('files', file, file.name));
            appendApplicationId(formDataToSend);

            const uploadRes = await axios.post(`${getApiBaseUrl()}/file/upload`, formDataToSend, {
              headers: { 'Content-Type': 'multipart/form-data' },
            });
            const uploadedList = uploadRes.data?.data || [];

            if (!uploadedList.length) {
              CreateAlert('No files were uploaded', 'Warning', setAlert);
              return;
            }

            if (draft.BannerId) {
              const uploaded = uploadedList[0];
              if (uploaded?.FilePath) {
                setDraft((p) => ({
                  ...p,
                  ImageUrl: buildImageUrlFromUpload(uploaded.FilePath),
                }));
                CreateAlert('Image URL updated from upload', 'Success', setAlert);
              }
              return;
            }

            let nextOrder = getNextDisplayOrder(bannerRows);
            let savedCount = 0;

            for (const uploaded of uploadedList) {
              if (!uploaded?.FilePath) continue;

              const response = await axios.post(`${getApiBaseUrl()}/ec-homepage-banner/save`, {
                BannerId: null,
                BannerType: bannerType,
                ImageUrl: buildImageUrlFromUpload(uploaded.FilePath).trim(),
                Title: null,
                ProductId: null,
                DisplayOrder: nextOrder,
                IsActive: draft.IsActive ? 1 : 0,
                UserId: getStorageValues('userId'),
              });

              const result = response.data?.data;
              if (result?.success === 1) {
                savedCount += 1;
                nextOrder += 1;
              }
            }

            if (savedCount > 0) {
              CreateAlert(
                savedCount === 1 ? '1 hero slide added' : `${savedCount} hero slides added`,
                'Success',
                setAlert,
              );
              resetDraft();
              await fetchBanners();
            } else {
              CreateAlert('Files uploaded but could not be saved', 'Error', setAlert);
            }
          } catch (err) {
            console.error(err);
            CreateAlert('Failed to upload images', 'Error', setAlert);
          }
        }}
      />
    </>
  );
}

export default HomepageBannerTab;
